import { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Container,
  Switch,
  Slider,
  FormControlLabel,
  Divider,
  TextField,
  Alert,
  Chip,
} from '@mui/material';
import {
  VolumeOff,
  VolumeUp,
  Timer,
  Tune,
  GraphicEq,
} from '@mui/icons-material';

interface AudioSettingsProps {
  onSettingsChange?: (settings: AudioSettingsConfig) => void;
}

export interface AudioSettingsConfig {
  silentModeDetection: boolean;
  silenceThreshold: number; // seconds
  silenceAction: 'pause' | 'prompt' | 'none';
  proximityFiltering: boolean;
  decibelThreshold: number; // dB
  distanceEstimate: number; // meters
  noiseReduction: boolean;
}

export default function AudioSettings({ onSettingsChange }: AudioSettingsProps) {
  const [settings, setSettings] = useState<AudioSettingsConfig>({
    silentModeDetection: true,
    silenceThreshold: 30,
    silenceAction: 'prompt',
    proximityFiltering: true,
    decibelThreshold: 60,
    distanceEstimate: 2,
    noiseReduction: true,
  });

  const [isDetectingSilence, setIsDetectingSilence] = useState(false);
  const [silenceDuration, setSilenceDuration] = useState(0);

  const handleSettingChange = (key: keyof AudioSettingsConfig, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    onSettingsChange?.(newSettings);
  };

  // Simulate silence detection (would integrate with actual audio monitoring)
  const simulateSilenceDetection = () => {
    setIsDetectingSilence(true);
    setSilenceDuration(0);

    const interval = setInterval(() => {
      setSilenceDuration((prev) => {
        const newDuration = prev + 1;
        if (newDuration >= settings.silenceThreshold) {
          clearInterval(interval);
          setIsDetectingSilence(false);
          if (settings.silenceAction === 'prompt') {
            alert('Are you still recording? The system detected prolonged silence.');
          }
          return 0;
        }
        return newDuration;
      });
    }, 1000);
  };

  return (
    <Box
      sx={{
        maxHeight: 'calc(100vh - 200px)',
        '@supports (height: 100dvh)': {
          maxHeight: 'calc(100dvh - 200px)',
        },
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#bdbdbd',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#9e9e9e',
          },
        },
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          {/* Silent Mode Detection */}
          <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Timer sx={{ color: '#1976d2' }} />
          <Typography variant="h6">Silent Mode Detection</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Automatically detect inactivity and prompt user when no audio is detected for a specified duration
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={settings.silentModeDetection}
              onChange={(e) => handleSettingChange('silentModeDetection', e.target.checked)}
              color="primary"
            />
          }
          label="Enable Silent Mode Detection"
        />

        {settings.silentModeDetection && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              Silence Threshold: {settings.silenceThreshold} seconds
            </Typography>
            <Slider
              value={settings.silenceThreshold}
              onChange={(_e, value) => handleSettingChange('silenceThreshold', value)}
              min={10}
              max={120}
              step={5}
              marks={[
                { value: 10, label: '10s' },
                { value: 30, label: '30s' },
                { value: 60, label: '1min' },
                { value: 120, label: '2min' },
              ]}
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" gutterBottom>
                Action on Silence
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip
                  label="Prompt User"
                  onClick={() => handleSettingChange('silenceAction', 'prompt')}
                  color={settings.silenceAction === 'prompt' ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Auto Pause"
                  onClick={() => handleSettingChange('silenceAction', 'pause')}
                  color={settings.silenceAction === 'pause' ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="No Action"
                  onClick={() => handleSettingChange('silenceAction', 'none')}
                  color={settings.silenceAction === 'none' ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>

            {isDetectingSilence && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Silence detected: {silenceDuration}s / {settings.silenceThreshold}s
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Proximity-Based Audio Filtering */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GraphicEq sx={{ color: '#009688' }} />
          <Typography variant="h6">Proximity-Based Audio Filtering</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Filter out distant conversations and background noise based on volume thresholds
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={settings.proximityFiltering}
              onChange={(e) => handleSettingChange('proximityFiltering', e.target.checked)}
              color="success"
            />
          }
          label="Enable Proximity Filtering"
        />

        {settings.proximityFiltering && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" gutterBottom>
              Decibel Threshold: {settings.decibelThreshold} dB
            </Typography>
            <Slider
              value={settings.decibelThreshold}
              onChange={(_e, value) => handleSettingChange('decibelThreshold', value)}
              min={40}
              max={90}
              step={5}
              marks={[
                { value: 40, label: 'Quiet' },
                { value: 60, label: 'Normal' },
                { value: 80, label: 'Loud' },
              ]}
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" gutterBottom>
                Estimated Distance Filter: {settings.distanceEstimate} meters
              </Typography>
              <Slider
                value={settings.distanceEstimate}
                onChange={(_e, value) => handleSettingChange('distanceEstimate', value)}
                min={0.5}
                max={5}
                step={0.5}
                marks={[
                  { value: 0.5, label: '0.5m' },
                  { value: 2, label: '2m' },
                  { value: 5, label: '5m' },
                ]}
                sx={{ mt: 2 }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={settings.noiseReduction}
                  onChange={(e) => handleSettingChange('noiseReduction', e.target.checked)}
                  color="success"
                />
              }
              label="Background Noise Reduction"
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>How it works:</strong> Audio below {settings.decibelThreshold} dB or estimated to
                be further than {settings.distanceEstimate}m will be filtered out, focusing on primary
                speakers.
              </Typography>
            </Alert>
          </Box>
        )}
      </Paper>

      {/* Audio Configuration Summary */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Tune sx={{ color: '#757575' }} />
          <Typography variant="h6">Configuration Summary</Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Silent Mode Detection:
            </Typography>
            <Chip
              label={settings.silentModeDetection ? 'Enabled' : 'Disabled'}
              size="small"
              color={settings.silentModeDetection ? 'primary' : 'default'}
            />
          </Box>

          {settings.silentModeDetection && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Silence Threshold:
                </Typography>
                <Typography variant="body2">{settings.silenceThreshold}s</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Silence Action:
                </Typography>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {settings.silenceAction}
                </Typography>
              </Box>
            </>
          )}

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Proximity Filtering:
            </Typography>
            <Chip
              label={settings.proximityFiltering ? 'Enabled' : 'Disabled'}
              size="small"
              color={settings.proximityFiltering ? 'success' : 'default'}
            />
          </Box>

          {settings.proximityFiltering && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Decibel Threshold:
                </Typography>
                <Typography variant="body2">{settings.decibelThreshold} dB</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Distance Filter:
                </Typography>
                <Typography variant="body2">{settings.distanceEstimate}m</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Noise Reduction:
                </Typography>
                <Chip
                  label={settings.noiseReduction ? 'On' : 'Off'}
                  size="small"
                  color={settings.noiseReduction ? 'success' : 'default'}
                />
              </Box>
            </>
          )}
        </Box>
      </Paper>
        </Box>
      </Container>
    </Box>
  );
}
